import { useCallback, useMemo, useRef } from 'react';
import { track } from '@amplitude/analytics-browser';

import CreditNotification from './components/CreditNotification/CreditNotification';
import CreditPaymentInfo from './components/CreditPaymentInfo';
import CreditOverview from './components/CreditOverview';
import PaymentForm, { PaymentFormValues } from './components/PaymentForm';
import { AmplitudeEvents } from '@utils/amplitudeEvents';
import {
	MoneyCurrencyEnum,
	CreditLineAccountV2Response,
	RepaymentPaymentAccountAccountTypeEnum,
	CreditLineState,
} from '../../types/generated/pos';
import { usePostPaymentApi } from '@api/services/accountService/usePostPaymentApi';
import { getCreditEventOptions } from './utils/eventOptions';
import { getCreditMinPayment } from './utils/getCreditMinPayment';
import { AmountSuggestParams } from './components/AmountInput/utils/suggestTypes';
import { usePaymentRedirect } from './utils/usePaymentRedirect';
import { PaymentTitle } from './components/PaymentTitle';
import { Caption } from './components/Caption';
import RegulatedBy, { LoanOnlineProducts } from './components/RegulatedBy';
import { useGetProviders } from '@api/services/providers/getProviders';
import { getProviderIdByCode } from './utils/getProviderIdByCode';
interface Props {
	credit: CreditLineAccountV2Response;
}

function CreditAccount(props: Props) {
	const { credit } = props;

	const creditState: CreditLineState = credit?.state;

	const [{ data: paymentData, error: paymentError, loading }, mutate] =
		usePostPaymentApi();

	const { data: providerListResponse } = useGetProviders();

	const lastProviderCodeRef = useRef<string>('');

	const eventOptions = getCreditEventOptions(credit, creditState);

	const initialValues: Partial<PaymentFormValues> = useMemo(() => {
		return {
			amount: getCreditMinPayment(creditState)?.amount,
			providerCode: undefined,
		};
	}, [creditState]);

	const amountSuggest: AmountSuggestParams = useMemo(() => {
		return {
			requiredPayment: getCreditMinPayment(creditState),
			outstandingBalance: credit.balance.outstanding,
		};
	}, [creditState, credit.balance.outstanding]);

	const handleSubmit = useCallback(
		({ providerCode, amount }: PaymentFormValues) => {
			const providerId = getProviderIdByCode(
				providerListResponse?.providers,
				providerCode
			);

			if (!providerId) {
				console.error('Provider ID not found for code:', providerCode);
				return;
			}

			lastProviderCodeRef.current = providerCode;

			track(AmplitudeEvents.GoToPayment_Tap, { product: eventOptions });

			mutate({
				providerId,
				amount: { amount, currency: MoneyCurrencyEnum.PHP },
				from: {
					accountId: credit.id,
					accountType: RepaymentPaymentAccountAccountTypeEnum.CREDIT_LINE,
				},
			});
		},
		[eventOptions, providerListResponse?.providers, mutate, credit.id]
	);

	usePaymentRedirect(paymentData, {
		accountId: credit.id,
		providerCode: lastProviderCodeRef.current,
	});

	return (
		<div>
			<Caption>Salmon Credit</Caption>

			<CreditPaymentInfo availableBalance={credit.balance.available} />

			<PaymentTitle>Payment</PaymentTitle>
			<CreditNotification credit={credit} creditState={creditState} />

			<PaymentForm
				onSubmit={handleSubmit}
				initialValues={initialValues}
				eventOptions={eventOptions}
				amountSuggest={amountSuggest}
				serverError={paymentError}
				loading={loading}
			/>

			<CreditOverview credit={credit} />
			<RegulatedBy productType={LoanOnlineProducts.SALMON_CREDIT} />
		</div>
	);
}

export default CreditAccount;
